/* eslint-disable no-nested-ternary */
import React from 'react'
import PropTypes from 'prop-types'
import { ariaProps, dataProps, useUniqueId } from '@ds/react-utils'
import { InputDescription } from '../../../components/InputDescription'
import { useIsInk, useThemeStyles } from '../../../theming'
import type {
  AriaAttributes,
  ConditionalReactElementChildOrArray,
} from '../../../types'
import { AboveInputContainer } from '../AboveInputContainer'
import { BelowInputContainer } from '../BelowInputContainer'
import { InputRequiredAsterisk } from '../InputRequiredAsterisk'
import type { TextEllipsisProps } from '../../../components/TextEllipsis'
import styles from './styles'

export interface SelectionControlGroupProps extends AriaAttributes {
  /**
   * Any visually hidden text that needs to go inside the legend.
   */
  accessibilityText?: string
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * Applies a 'description' treatment, displaying the provided text.
   */
  description?: string
  /**
   * Adds a disabled style to the legend element
   */
  disabled?: boolean
  /**
   * Applies an 'error' treatment, displaying the provided text.
   */
  error?: string
  /**
   * Hide the SelectControLGroup legend while allowing assistive devices to identify the control.
   */
  hideLegend?: boolean
  /**
   * The legend for the selection control group.
   * Accepts a string or a `<TextEllipsis />` component if truncation is required.
   */
  legend?: string | React.ReactElement<TextEllipsisProps>
  /**
   * Function to render the children which should loop through and add the
   *  'selectionControlGroupItem' and 'selectionControlGroupNestedWrap' CSS classes.
   */
  renderChildren: <T>() => ConditionalReactElementChildOrArray<T>
  /**
   * Adds a visual treatment to the legend indicating it is required.
   */
  required?: boolean
  /**
   * ONLY for CheckboxGroup components. The provided string will not display, but is used
   * for accessibility to describe the required text.
   */
  selectionRequired?: string
  /**
   * Define what type of user interface element this is for assistive technologies.
   */
  role?: string
}

export function SelectionControlGroup({
  'data-qa': dataQa,
  description,
  disabled = false,
  error,
  hideLegend = false,
  legend,
  accessibilityText,
  renderChildren,
  required = false,
  role,
  selectionRequired,
  ...restProps
}: SelectionControlGroupProps) {
  const sx = useThemeStyles(styles, {
    description,
  })

  const descriptionId = useUniqueId('short')
  const isInk = useIsInk()

  const hiddenLegendStyles = [sx.hiddenLegend, sx.hidden]

  const legendStyles = [
    sx.default.legend,
    disabled && sx.disabled.legend,
    (!legend || hideLegend) && hiddenLegendStyles,
  ]

  const legendNode = (legend ||
    description ||
    selectionRequired ||
    error ||
    required) && (
    <legend css={legendStyles} data-qa={dataQa && `${dataQa}-legend`}>
      {legend && accessibilityText ? (
        <span aria-hidden="true" data-qa={dataQa && `${dataQa}-legend-text`}>
          {legend}
        </span>
      ) : (
        legend
      )}
      {accessibilityText && (
        <span
          css={sx.hidden}
          data-qa={dataQa && `${dataQa}-accessibility-text`}
        >
          {accessibilityText}
        </span>
      )}
      {required && (
        <InputRequiredAsterisk data-qa={dataQa && `${dataQa}-required`} />
      )}
      {description && (
        <span
          css={sx.hidden}
          data-qa={dataQa && `${dataQa}-description-legend`}
        >{`, ${description}`}</span>
      )}
      {selectionRequired && (
        <span
          css={sx.hidden}
          data-qa={dataQa && `${dataQa}-selection-required-legend`}
        >{`, ${selectionRequired}`}</span>
      )}
      {error && (
        <span
          css={sx.hidden}
          data-qa={dataQa && `${dataQa}-error-legend`}
        >{`, ${error}`}</span>
      )}
    </legend>
  )

  const DescriptionNode = description && (
    <InputDescription
      aria-hidden="true"
      disabled={disabled}
      id={descriptionId}
      kind="helper"
      data-qa={dataQa && `${dataQa}-description`}
    >
      {description}
    </InputDescription>
  )

  const errorNode = error && (
    <InputDescription
      aria-hidden="true"
      id={descriptionId}
      kind="error"
      data-qa={dataQa && `${dataQa}-error`}
    >
      {error}
    </InputDescription>
  )

  return (
    <fieldset
      {...ariaProps(restProps)}
      {...dataProps(restProps)}
      aria-invalid={role !== undefined && error ? 'true' : undefined}
      aria-required={role !== undefined && required ? 'true' : undefined}
      css={sx.default.fieldset}
      data-qa={dataQa}
      disabled={disabled}
      role={role}
    >
      {legendNode}

      <AboveInputContainer
        description={isInk && DescriptionNode ? DescriptionNode : undefined}
      />

      {renderChildren()}

      <BelowInputContainer
        description={!isInk && DescriptionNode ? DescriptionNode : undefined}
        error={errorNode || undefined}
      />
    </fieldset>
  )
}

SelectionControlGroup.propTypes = {
  accessibilityText: PropTypes.string,
  'data-.*': PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  hideLegend: PropTypes.bool,
  legend: PropTypes.string,
  renderChildren: PropTypes.func.isRequired,
  required: PropTypes.bool,
  role: PropTypes.string,
}

SelectionControlGroup.displayName = 'SelectionControlGroup'
